import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { CreateCatalogRequest } from "@/services/backend/qpm/catalog/types/catalog";
import { useState } from "react";
import { Textarea } from "@/shared/components/ui/textarea";
import { formatDate } from "@/shared/lib/utilities/date";
import {
  useQpmCatalogCopyItemsMutation,
  useQpmCatalogCreateMutation,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import { useNavigate } from "react-router";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { CatalogSourceSelect } from "@/routes/base-data/processes/qpm/catalog/_components/catalog-source-select";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

const CATALOG_OVERVIEW_PATH = "/base-data/processes/qpm/catalog";

export function CreateNewCatalogDialog({
  requireCustomer = false,
  triggerLabel,
  dialogTitle,
}: {
  requireCustomer?: boolean;
  triggerLabel?: string;
  dialogTitle?: string;
} = {}) {
  const { toast } = useToast();
  const { companyId } = useActiveCompany();
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [templateCatalogId, setTemplateCatalogId] = useState("");
  const [request, setRequest] = useState<CreateCatalogRequest>({
    companyId,
    name: "",
    description: "",
  });

  const [createCatalog, createState] = useQpmCatalogCreateMutation();
  const [copyItems, copyState] = useQpmCatalogCopyItemsMutation();
  const isLoading = createState.isLoading || copyState.isLoading;

  const resetForm = () => {
    setRequest({
      companyId,
      name: "",
      description: "",
    });
    setTemplateCatalogId("");
    createState.reset();
    copyState.reset();
  };

  const onCreate = async () => {
    if (!request.name.trim() || (requireCustomer && !request.customerId)) {
      return;
    }

    try {
      const createdCatalog = await createCatalog({
        ...request,
        companyId,
        name: request.name.trim(),
      }).unwrap();

      if (templateCatalogId) {
        await copyItems({
          sourceCatalogId: templateCatalogId,
          targetCatalogId: createdCatalog.id,
        }).unwrap();
      }

      toast({
        title: t("Katalog erstellt"),
        description: templateCatalogId
          ? t("Der Katalog wurde aus der Vorlage erstellt.")
          : t("Der Katalog wurde erstellt."),
      });

      setOpen(false);
      resetForm();
      navigate(`${CATALOG_OVERVIEW_PATH}/${createdCatalog.id}`);
    } catch (err) {
      const parsedError = parseRTKQueryError(
        err as Parameters<typeof parseRTKQueryError>[0],
      );
      if (parsedError) {
        toast({
          title: parsedError.title,
          description: parsedError.description,
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          resetForm();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="default">
          <Plus />
          {triggerLabel ?? t("Katalog hinzufügen")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-screen-md">
        <DialogHeader>
          <DialogTitle>
            {dialogTitle ?? t("Neuen Katalog erstellen")}
          </DialogTitle>
          <DialogDescription>
            {t("Sie können hier einen neuen Katalog anlegen.")}
            <br />
            {t(
              "Vergeben Sie hierzu einen Namen und optional eine Beschreibung.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <div>
            <Label htmlFor="name">{t("Name")}</Label>
            <Input
              id="name"
              type="text"
              value={request.name}
              onChange={(val) => {
                setRequest((prev) => ({ ...prev, name: val.target.value }));
              }}
            />
          </div>
          <div>
            <Label htmlFor="description">{t("Beschreibung")}</Label>
            <Textarea
              id="description"
              value={request.description}
              onChange={(val) => {
                setRequest((prev) => ({
                  ...prev,
                  description: val.target.value,
                }));
              }}
            />
          </div>
          <div>
            <Label htmlFor="customer">
              {requireCustomer ? t("Kunde") : t("Kunde (optional)")}
            </Label>
            <CustomerSelect
              value={
                request.customerId ?? (requireCustomer ? undefined : NullUUID)
              }
              showUnsetOption={!requireCustomer}
              onValueChange={(customerId) =>
                setRequest((prev) => ({
                  ...prev,
                  customerId:
                    customerId && customerId !== NullUUID
                      ? customerId
                      : undefined,
                }))
              }
            />
          </div>
          <div>
            <Label htmlFor="templateCatalog">
              {t("Vorlagekatalog (optional)")}
            </Label>
            <CatalogSourceSelect
              value={templateCatalogId}
              onChange={setTemplateCatalogId}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="createdAt">{t("Erstellungsdatum")}</Label>
            <Input
              className="w-fit text-end"
              id="createdAt"
              value={formatDate(new Date().toISOString())}
              disabled
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="default"
            onClick={onCreate}
            disabled={
              !request.name.trim() ||
              (requireCustomer && !request.customerId) ||
              isLoading
            }
          >
            {templateCatalogId
              ? t("Erstellen und kopieren")
              : t("Katalog erstellen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
